import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { string, object, date, array } from 'yup'
import { gql } from '@apollo/client'
import moment from 'moment'

import client, {deserializeErrors} from 'apollo-client'
import { trackEvent } from 'analytics'
import { useSites, useUser } from 'store'
import { BYPASSES_DISCLAIMER_KEY } from 'variables'

export const REGEX_MAC_ADDRESS = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/

export const buildActivateSchema = ({t}) => {
  return object({
    deactivated_at: date()
      .nullable()
      .when(['permanently'], {
        is: (value) => !value,
        then: (schema) => schema.required(t('ui_elements.required_field')),
        otherwise: schema => schema.notRequired()
      })
  })
}

export const buildUnitSchema = ({t}) => {
  return object({
    mac_address: string()
      .required(t('ui_elements.required_field'))
      .matches(REGEX_MAC_ADDRESS, {
        message: t('errors.bypass.mac_address.bad_format')
      })
    ,
    deactivated_at: date()
      .nullable()
      .when(['permanently'], {
        is: (value) => !value,
        then: (schema) => schema.required(t('ui_elements.required_field')),
        otherwise: schema => schema.notRequired()
      })
  })
}

export const buildImportSchema = ({t}) => {
  return object({
    lines: array()
      .min(1, t('bypass.import.error_field_required'))
      .of(
        object({
          mac_address: string()
            .required(t('ui_elements.required_field'))
            .matches(REGEX_MAC_ADDRESS, {
              message: t('errors.bypass.mac_address.bad_format')
            })
          ,
        })
      )
    ,
    deactivated_at: date()
      .nullable()
      .when(['permanently'], {
        is: (value) => !value,
        then: (schema) => schema.required(t('ui_elements.required_field')),
        otherwise: schema => schema.notRequired()
      })
  })
}

const OPERATION_IMPORT = gql`
  mutation ImportBypasses($params: BypassesImportInput) {
    bypasses: importBypasses(params: $params) {
      id
    }
  }
`
const OPERATION_DEACTIVATE_MULTI = gql`
  mutation DeactivateBypasses($ids: [ID!], $deactivated_at: String) {
    bypasses: deactivateBypasses(ids: $ids, deactivated_at: $deactivated_at) {
      id     
    }
  }
`

const OPERATION_ACTIVATE_MULTI = gql`
  mutation ActivateBypasses($ids: [ID!], $deactivated_at: String) {
    bypass: activateBypasses(ids: $ids, deactivated_at: $deactivated_at) {
      id     
    }
  }
`

const OPERATION_DEACTIVATE = gql`
  mutation DeactivateBypass($id: ID!, $deactivated_at: String) {
    bypasses: deactivateBypass(id: $id, deactivated_at: $deactivated_at) {
      id     
    }
  }
`

const OPERATION_ACTIVATE = gql`
  mutation ActivateBypass($id: ID!, $deactivated_at: String) {
    bypass: activateBypass(id: $id, deactivated_at: $deactivated_at) {
      id     
    }
  }
`

const OPERATION_REMOVE = gql`
  mutation RemoveBypass($id: ID!) {
    result: removeBypass(id: $id)
  }
`

const OPERATION_REMOVE_MULTI = gql`
  mutation RemoveBypasses($ids: [ID!]) {
    result: removeBypasses(ids: $ids)
  }
`

const OPERATION_CREATE = gql`
  mutation CreateBypass($sfId: ID!, $mac_address: String!, $description: String, $deactivated_at: String) {
    bypass: createBypass(site_sf_id: $sfId, mac_address: $mac_address, description: $description, deactivated_at: $deactivated_at) {
      id
    }
  }
`

const OPERATION_UPDATE = gql`
  mutation UpdateBypass($id: ID!, $description: String, $deactivated_at: String) {
    bypass: updateBypass(id: $id, description: $description, deactivated_at: $deactivated_at) {
      id
    }
  }
`

const OPERATION_DEACTIVATE_DISCLAIMER = gql`
  mutation DeactivateDisclaimer {
    result: deactivateDisclaimer
  }
`

interface IImportLaunchInput {
  deactivated_at?: string
  lines: {mac_address: string, description?: string}[]
}

export const useImport = ():[({ deactivated_at, lines }: IImportLaunchInput) => Promise<{error?: string}>, {
  loading: boolean;
  error: string | null
}] => {
  const [{selected}] = useSites()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const launch = async ({ deactivated_at, lines }: IImportLaunchInput): Promise<{ error?: string }> => {
    setLoading(true)
    setError(null)
    try {
      await client.mutate({
        mutation: OPERATION_IMPORT,
        variables: {
          params: {
            site_sf_id: selected[0].sf_id,
            deactivated_at: deactivated_at ? moment(deactivated_at).endOf('day').toISOString() : null,
            lines
          }
        },
        refetchQueries: ['bypass', 'bypasses', 'countBypasses']
      })
      trackEvent('bypass_imported', { item_count: lines.length, permanent: !deactivated_at })
      return {}
    } catch (e) {
      // @ts-expect-error from JSX
      const [part1, part2] = deserializeErrors(e).split('::')
      setError(part2 || part1)
      return { error: part2 || part1 }
    } finally {
      setLoading(false)
    }
  }
  return [
    launch,
    {loading, error}
  ]
}

export const useRemove = ():[() => Promise<{error?: string}>, {loading: boolean}] => {
  const [loading, setLoading] = useState<boolean>(false)
  const {id} = useParams()
  const remove = async () => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_REMOVE,
        variables: {id},
        refetchQueries: [
          'bypasses',
          'countBypasses'
        ]
      })
      trackEvent('bypass_removed', { action_scope: 'item' })
      return {  }
    } catch (e) {
      // @ts-expect-error from JSX
      const [part1, part2] = deserializeErrors(e).split('::')
      return { error: part2 || part1 }
    }
    finally {
      setLoading(false)
    }
  }
  return [remove, {loading}]
}

export const useRemoveMulti = ():[(v:string[]) => Promise<{ error?: unknown }>, {loading: boolean}] => {
  const [loading, setLoading] = useState<boolean>(false)
  const remove = async (ids) => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_REMOVE_MULTI,
        variables: {ids},
        refetchQueries: [
          'bypasses',
          'countBypasses'
        ]
      })
      trackEvent('bypass_removed', { action_scope: 'bulk', item_count: ids.length })
      return {}
    } catch (e) {
      // @ts-expect-error from JSX
      const [part1,part2] = deserializeErrors(e).split('::')
      return {
        error: part2 || part1
      }
    }
    finally {
      setLoading(false)
    }
  }
  return [remove, {loading}]
}

export const useDeactivateMulti = ():[(ids: string[]) => Promise<{ error?: unknown }>, { loading: boolean }] => {
  const [loading, setLoading] = useState<boolean>(false)

  const deactivate = async (ids: string[]) => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_DEACTIVATE_MULTI,
        variables: {ids},
        refetchQueries: [
          'bypasses',
          'countBypasses'
        ]
      })
      trackEvent('bypass_deactivated', { action_scope: 'bulk', item_count: ids.length })
      return {}
    } catch (e) {
      // @ts-expect-error JSX
      const [part1,part2] = deserializeErrors(e).split('::')
      return {
        error: part2 || part1
      }
    } finally {
      setLoading(false)
    }
  }

  return [deactivate, {loading}]
}

export const useDeactivate = ():[(id: string) => Promise<{ error?: unknown }>, { loading: boolean }] => {
  const [loading, setLoading] = useState<boolean>(false)

  const deactivate = async (id: string) => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_DEACTIVATE,
        variables: {id},
        refetchQueries: [
          'bypasses',
          'bypass',
          'countBypasses'
        ]
      })
      trackEvent('bypass_deactivated', { action_scope: 'item' })
      return {}
    } catch (e) {
      // @ts-expect-error JSX
      const [part1,part2] = deserializeErrors(e).split('::')
      return {
        error: part2 || part1
      }
    } finally {
      setLoading(false)
    }
  }

  return [deactivate, {loading}]
}

export const useActivateMulti = ():[(ids: string[], deactivated_at?: string) => Promise<{ error?: unknown }>, { loading: boolean }] => {
  const [loading, setLoading] = useState<boolean>(false)

  const activate = async (ids: string[], deactivated_at?: string) => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_ACTIVATE_MULTI,
        variables: {
          ids, 
          deactivated_at: deactivated_at ? moment(deactivated_at).endOf('day').toISOString() : null
        },
        refetchQueries: [
          'bypasses',
          'countBypasses'
        ]
      })
      trackEvent('bypass_activated', { action_scope: 'bulk', item_count: ids.length, permanent: !deactivated_at })
      return {}
    } catch (e) {
      // @ts-expect-error JSX
      const [part1,part2] = deserializeErrors(e).split('::')
      return {
        error: part2 || part1
      }
    } finally {
      setLoading(false)
    }
  }

  return [activate, {loading}]
}

export const useActivate = ():[(id: string, deactivated_at?: string) => Promise<{ error?: unknown }>, { loading: boolean }] => {
  const [loading, setLoading] = useState<boolean>(false)

  const activate = async (id: string, deactivated_at?: string) => {
    setLoading(true)
    try {
      await client.mutate({
        mutation: OPERATION_ACTIVATE,
        variables: {
          id,
          deactivated_at: deactivated_at ? moment(deactivated_at).endOf('day').toISOString() : null
        },
        refetchQueries: [
          'bypasses',
          'bypass',
          'countBypasses'
        ]
      })
      trackEvent('bypass_activated', { action_scope: 'item', permanent: !deactivated_at })
      return {}
    } catch (e) {
      // @ts-expect-error JSX
      const [part1,part2] = deserializeErrors(e).split('::')
      return {
        error: part2 || part1
      }
    } finally {
      setLoading(false)
    }
  }

  return [activate, {loading}]
}

export const useDeactivateDisclaimer = ():[() => Promise<{error?: string}>, {loading: boolean}] => {
  const [loading, setLoading] = useState<boolean>(false)
  const [, setUser] = useUser()
  const deactivateDisclaimer = async () => {
    setLoading(true)
    try {
      const response = await client.mutate({
        mutation: OPERATION_DEACTIVATE_DISCLAIMER,
      })
      if (response.data.result) {
        setUser(prev => prev ? { ...prev, [BYPASSES_DISCLAIMER_KEY]: false } : prev)
        localStorage.setItem(BYPASSES_DISCLAIMER_KEY, 'false')
        trackEvent('bypass_disclaimer_dismissed')
      }
      console.log(response)
      return {}
    } catch (e) {
      // @ts-expect-error JSX
      const [part1,part2] = deserializeErrors(e).split('::')
      return {
        error: part2 || part1
      }
    } finally {
      setLoading(false)
    }
  }
  return [deactivateDisclaimer, {loading}]
}

const useSave = ():[(any) => Promise<{error?: string}>, {loading: boolean, error: string|null}] => {
  const [{selected}] = useSites()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const execMutation = async ({id, ...bypass}) => {
    let response
    if (id) {
      response = await client.mutate({
        mutation: OPERATION_UPDATE,
        variables: {
          id,
          description: bypass.description,
          deactivated_at: bypass.deactivated_at ? moment(bypass.deactivated_at).endOf('day').toISOString() : null,
        },
        refetchQueries: [
          'bypasses',
          'bypass',
          'countBypasses'
        ]
      })
    } else {
      response = await client.mutate({
        mutation: OPERATION_CREATE,
        variables: {
          sfId: selected[0].sf_id,
          mac_address: bypass.mac_address,
          description: bypass.description,
          deactivated_at: bypass.deactivated_at ? moment(bypass.deactivated_at).endOf('day').toISOString() : null,
        },
        refetchQueries: [
          'bypasses',
          'countBypasses'
        ]
      })
    }
    return response.data.bypass
  }

  const save = async (values) => {
    setLoading(true)
    setError(null)
    try {
      const bypass = await execMutation(values)
      trackEvent(values.id ? 'bypass_updated' : 'bypass_created', {
        has_description: Boolean(values.description),
        permanent: !values.deactivated_at,
      })
      return { data: bypass }
    } catch (e) {
      // @ts-expect-error JSX
      const [part1,part2] = deserializeErrors(e).split('::')
      setError(part2 || part1)
      return { error: part2 || part1 }
    } finally {
      setLoading(false)
    }
  }

  return [save, {loading, error}]
}

export default useSave